/**
 * Polling that only runs while someone can see it — the rules the init
 * report, the sync watch and the pipeline polls (useInitReport.ts,
 * useSyncWatch.ts, useRunningPipelines.ts, BranchPage.tsx) each spell out,
 * in one hook for the other polls of the app:
 *  - one read at a time: the next is scheduled once the previous one has
 *    settled (a setTimeout chain, not an interval);
 *  - paused while the document is hidden — a background tab costs no
 *    request — and resumed with an immediate read when it is shown again,
 *    then at the usual cadence;
 *  - stopped as soon as it is disabled or unmounted; a read in flight then
 *    finishes, and nothing follows it.
 */

import { useEffect, useRef } from 'react';

/**
 * Calls `poll` every `intervalMs` while `enabled` and the document is
 * visible — the first call one interval after it is enabled. `poll` may
 * return a promise: the next call waits for it. Its rejection is swallowed;
 * a poll handles its own failures.
 */
export function useVisiblePolling(
  poll: () => void | Promise<unknown>,
  intervalMs: number,
  enabled: boolean,
): void {
  // The latest function, read by the timer without restarting it: callers
  // build it anew on every render.
  const latest = useRef(poll);
  latest.current = poll;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let inFlight = false;

    // The next read, while someone can see it.
    const schedule = () => {
      if (active && timer === undefined && !inFlight && !document.hidden) {
        timer = setTimeout(() => {
          timer = undefined;
          if (active) {
            run();
          }
        }, intervalMs);
      }
    };

    const settled = () => {
      inFlight = false;
      schedule();
    };

    function run() {
      inFlight = true;
      let result: void | Promise<unknown>;
      try {
        result = latest.current();
      } catch {
        result = undefined;
      }
      // A synchronous poll schedules the next read at once rather than in a
      // microtask: fake timers advanced synchronously (the tests) see it.
      if (result instanceof Promise) {
        result.then(settled, settled);
      } else {
        settled();
      }
    }

    const onVisibilityChange = () => {
      if (document.hidden) {
        clearTimeout(timer);
        timer = undefined;
      } else if (timer === undefined && !inFlight) {
        run();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    schedule();
    return () => {
      active = false;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, intervalMs]);
}

/**
 * Calls `onVisible` each time the document is shown again after being
 * hidden — a return to the tab. Not on mount.
 */
export function useOnDocumentVisible(onVisible: () => void): void {
  const latest = useRef(onVisible);
  latest.current = onVisible;

  useEffect(() => {
    const onVisibilityChange = () => {
      if (!document.hidden) {
        latest.current();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);
}
